import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Badge, ProgressBar, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const WeddingPlanScreen = () => {
  const navigate = useNavigate();
  const [planData, setPlanData] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [copied, setCopied] = useState(false);
  const [activeVenuePhotoIdx, setActiveVenuePhotoIdx] = useState(0);
  const [activeVendorPhotoIdx, setActiveVendorPhotoIdx] = useState({}); // { catering: 0, photography: 0, ... }
  const printRef = useRef();

  // Get all photos for a vendor (primary + gallery)
  const getVendorPhotos = (vendor, fallback) => {
    const photos = [];
    if (vendor?.image) photos.push(vendor.image);
    if (vendor?.gallery) photos.push(...vendor.gallery);
    return photos.length > 0 ? photos : [fallback || '/images/venue.jpeg'];
  };

  // Swap a vendor in a category — recalculate totals
  const swapVendor = (category, newVendorId) => {
    if (!planData?.vendorDatabase?.[category]) return;
    const newVendor = planData.vendorDatabase[category].find(v => v.id === newVendorId);
    if (!newVendor) return;

    const guests = planData.guests || 50;
    let newPrice;
    if (newVendor.pricePerHead) {
      newPrice = Math.round(newVendor.pricePerHead * guests);
    } else {
      newPrice = newVendor.price || 0;
    }

    const updatedVendors = { ...planData.vendors, [category]: newVendor };
    const updatedAllocations = { ...planData.allocations, [category]: newPrice };

    // When venue changes, update catering based on resort vs villa
    if (category === 'venue') {
      const isResort = newVendor.venueType === 'resort';
      if (isResort) {
        updatedVendors.catering = { id: 'included', name: 'Included with resort venue', tier: newVendor.tier, pricePerHead: 0, features: ['Food & beverages included in venue package'] };
        updatedAllocations.catering = 0;
      } else if (planData.vendors?.catering?.id === 'included') {
        // Switching from resort to villa — restore a real caterer
        const db = planData.vendorDatabase?.catering;
        if (db && db.length > 0) {
          const caterer = db[0];
          updatedVendors.catering = caterer;
          updatedAllocations.catering = Math.round((caterer.pricePerHead || 0) * guests);
        }
      }
    }

    const updatedPlan = {
      ...planData,
      vendors: updatedVendors,
      allocations: updatedAllocations,
      total: Object.values(updatedAllocations).reduce((sum, v) => sum + v, 0)
    };
    setPlanData(updatedPlan);
    localStorage.setItem('weddingPlan', JSON.stringify(updatedPlan));
    if (category === 'venue') setActiveVenuePhotoIdx(0);
    setActiveVendorPhotoIdx(prev => ({ ...prev, [category]: 0 })); // Reset to first photo on vendor change
  };

  // Get ALL venue photos (primary + gallery) as one array
  const getAllVenuePhotos = () => {
    const vendor = planData?.vendors?.venue;
    const photos = [];
    if (vendor?.image) photos.push(vendor.image);
    if (vendor?.gallery) photos.push(...vendor.gallery);
    return photos.length > 0 ? photos : ['/images/venue.jpeg'];
  };

  const venuePhotos = planData ? getAllVenuePhotos() : ['/images/venue.jpeg'];
  const activeVenuePhoto = venuePhotos[activeVenuePhotoIdx] || venuePhotos[0];

  // Category images (non-venue categories use generic images for now)
  const categoryImages = {
    venue: '/images/venue.jpeg',
    catering: '/images/food.jpeg',
    photography: '/images/pic.jpeg',
    decoration: '/images/flowers.jpeg',
    entertainment: '/images/dj.jpeg'
  };

  useEffect(() => {
    // Load plan data from localStorage
    const savedPlan = localStorage.getItem('weddingPlan');
    if (savedPlan) {
      setPlanData(JSON.parse(savedPlan));
    } else {
      // Demo data if nothing saved
      setPlanData(getDemoData());
    }
  }, []);

  const getDemoData = () => ({
    packageName: 'Signature Experience',
    packageIcon: '⭐',
    packageColor: '#f59e0b',
    budget: 25000,
    total: 18500,
    guests: 80,
    createdAt: new Date().toISOString(),
    vendors: {
      venue: { id: 'villa-aurora', name: 'Villa Aurora', tier: 'signature', price: 2800, capacity: 120, features: ['Private estate', 'Pool terrace', 'AC indoor backup'], vibe: 'villa' },
      catering: { id: 'premium-plated', name: 'Culinary Artisans', tier: 'signature', pricePerHead: 85, features: ['5-course plated', 'Wine pairing', 'Chef\'s table option'], style: 'Fine Dining' },
      photography: { id: 'photo-signature', name: 'Eternal Moments', tier: 'signature', price: 1500, features: ['10 hours coverage', '500+ photos', 'Drone footage', 'Same-day preview'], deliverables: '500 photos + video' },
      decoration: { id: 'decor-romantic', name: 'Romance in Bloom', tier: 'signature', price: 2500, features: ['Premium roses', 'Floral arch', 'Table centerpieces', 'Aisle design'], style: 'Romantic Luxe' },
      entertainment: { id: 'ent-band', name: 'Bali Strings Quartet', tier: 'signature', price: 1200, features: ['Live ceremony music', 'Cocktail hour', 'First dance performance'], type: 'Live Band' }
    },
    allocations: {
      venue: 2800,
      catering: 6800,
      photography: 1500,
      decoration: 2500,
      entertainment: 1200
    },
    aiInsights: [
      { type: 'tip', text: 'Villa venues offer great photo opportunities at golden hour.' },
      { type: 'upgrade', text: 'Consider adding a drone videographer for stunning aerial shots.' },
      { type: 'savings', text: 'Book 6+ months in advance for up to 15% venue savings.' }
    ]
  });

  // Generate timeline based on package
  const generateTimeline = () => {
    if (!planData) return [];
    const isIntimate = planData.guests < 60;
    const isLuxury = planData.vendors?.venue?.tier === 'prestige';
    
    return [
      { time: '14:00', event: 'Venue Opens for Setup', icon: '🏝️', description: 'Our team begins final preparations' },
      { time: '15:00', event: 'Bridal Suite Ready', icon: '👰', description: 'Hair, makeup, and getting ready photos' },
      { time: '16:00', event: 'Guest Arrival & Welcome Drinks', icon: '🥂', description: 'Guests arrive and enjoy welcome cocktails' },
      { time: '16:30', event: 'Ceremony Begins', icon: '💍', description: isIntimate ? 'Intimate garden ceremony' : 'Grand ceremony with full seating' },
      { time: '17:00', event: 'Vows & Ring Exchange', icon: '💕', description: 'The magical moment you become one' },
      { time: '17:30', event: 'Cocktail Hour', icon: '🍹', description: 'Canapés and drinks while photos are taken' },
      { time: '18:30', event: 'Grand Entrance', icon: '✨', description: 'Make your entrance as newlyweds' },
      { time: '19:00', event: 'Dinner Service', icon: '🍽️', description: planData.vendors?.catering?.style || 'Fine dining experience' },
      { time: '20:30', event: 'Speeches & Toasts', icon: '🎤', description: 'Heartfelt words from loved ones' },
      { time: '21:00', event: 'First Dance', icon: '💃', description: planData.vendors?.entertainment?.type === 'Live Band' ? 'Live band performance' : 'DJ sets the mood' },
      { time: '21:30', event: 'Party Time', icon: '🎉', description: 'Dance floor opens for all guests' },
      { time: isLuxury ? '00:00' : '23:00', event: 'Farewell & Sparkler Send-off', icon: '🎇', description: 'Magical ending to your perfect day' }
    ];
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'My Bali Wedding Plan',
        text: `Check out our wedding plan: ${planData?.packageName}`,
        url: shareUrl
      });
    } else {
      setShowShareModal(true);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone,
          message: contactForm.message,
          weddingDate: e.target.querySelector('input[type="date"]')?.value || null,
          planData: planData
        })
      });
      const data = await res.json();
      if (data.success) {
        setContactSent(true);
      } else {
        alert(data.error || 'Failed to send. Please try WhatsApp instead.');
      }
    } catch (err) {
      alert('Failed to send. Please try WhatsApp instead.');
    }
    setContactSending(false);
  };

  if (!planData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d1117' }}>
        <div className="text-center">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✨</div>
          <p style={{ color: '#fff' }}>Loading your wedding plan...</p>
        </div>
      </div>
    );
  }

  const timeline = generateTimeline();
  const savings = planData.budget - planData.total;
  const isUnderBudget = savings >= 0;

  return (
    <>
      <Helmet>
        <title>Your Wedding Plan | Bali VIP Wedding</title>
        <meta name="description" content="Your personalized Bali wedding plan" />
      </Helmet>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { background: #fff !important; }
          .plan-page { background: #fff !important; color: #333 !important; }
        }
        .plan-page {
          background: linear-gradient(180deg, #0d1117 0%, #1a1a2e 50%, #0d1117 100%);
          min-height: 100vh;
          color: #fff;
        }
        .plan-hero {
          position: relative;
          padding: 4rem 0 3rem;
          text-align: center;
          background: linear-gradient(180deg, rgba(0,0,0,0.5), transparent);
        }
        .plan-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 1.5rem;
        }
        .vendor-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          overflow: hidden;
          height: 100%;
          transition: transform 200ms, box-shadow 200ms;
        }
        .vendor-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
        }
        .vendor-card img {
          width: 100%;
          height: 180px;
          object-fit: cover;
        }
        .timeline-item {
          display: flex;
          gap: 1.5rem;
          padding: 1.25rem 0;
          border-left: 2px solid rgba(255,255,255,0.1);
          margin-left: 1rem;
          padding-left: 2rem;
          position: relative;
        }
        .timeline-item::before {
          content: '';
          position: absolute;
          left: -8px;
          top: 1.5rem;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #10b981, #059669);
          border: 3px solid #0d1117;
        }
        .budget-bar {
          display: flex;
          height: 24px;
          border-radius: 12px;
          overflow: hidden;
          margin: 1rem 0;
        }
        .budget-bar-segment {
          transition: width 500ms;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 600;
          color: #fff;
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          margin-top: 1rem;
        }
        .gallery-grid img {
          width: 100%;
          height: 100px;
          object-fit: cover;
          border-radius: 8px;
          opacity: 0.8;
          transition: opacity 200ms;
        }
        .gallery-grid img:hover {
          opacity: 1;
        }
        .cta-sticky {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(13,17,23,1) 0%, rgba(13,17,23,0.95) 80%, transparent 100%);
          padding: 1.5rem 0 1rem;
          z-index: 100;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>

      <div className="plan-page" ref={printRef}>
        {/* Hero Section */}
        <div className="plan-hero">
          <Container>
            <Badge 
              bg="" 
              style={{ 
                background: `${planData.packageColor}20`, 
                color: planData.packageColor, 
                fontSize: '0.8rem', 
                padding: '0.5rem 1.25rem', 
                borderRadius: '20px',
                marginBottom: '1rem'
              }}
            >
              {planData.packageIcon} {planData.packageName}
            </Badge>
            <h1 style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: 'clamp(2rem, 5vw, 3.5rem)', 
              marginBottom: '0.5rem',
              letterSpacing: '-1px'
            }}>
              Your Bali Wedding Plan
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
              A personalized wedding experience crafted by AI, tailored to your vision and budget.
            </p>
            <div style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '10px', padding: '0.75rem 1.25rem', maxWidth: '560px', margin: '0 auto 2rem' }}>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
                ⚠️ <strong style={{ color: '#d4af37' }}>Estimate Only</strong> — Prices shown are approximate and based on current vendor rates. Final pricing may vary based on availability, season, and specific requirements. For accurate real-time pricing, please <span style={{ color: '#d4af37', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowContactModal(true)}>contact our planning team</span>.
              </p>
            </div>
            
            {/* Quick Stats */}
            <Row className="justify-content-center g-3" style={{ maxWidth: '700px', margin: '0 auto' }}>
              <Col xs={6} md={3}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: planData.packageColor }}>${planData.total.toLocaleString()}</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Cost</div>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff' }}>{planData.guests}</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Guests</div>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: isUnderBudget ? '#10b981' : '#ef4444' }}>
                    {isUnderBudget ? `-$${savings.toLocaleString()}` : `+$${Math.abs(savings).toLocaleString()}`}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {isUnderBudget ? 'Under Budget' : 'Over Budget'}
                  </div>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1rem' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff' }}>5</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>Vendors</div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        <Container style={{ paddingBottom: '120px' }}>
          {/* Venue Section - Featured */}
          <div className="plan-card" style={{ padding: 0, overflow: 'hidden' }}>
            <Row className="g-0">
              <Col lg={6} style={{ position: 'relative' }}>
                <img 
                  src={activeVenuePhoto}
                  alt={planData.vendors?.venue?.name}
                  style={{ width: '100%', height: '100%', minHeight: '400px', objectFit: 'cover' }}
                />
                {/* Left/Right navigation arrows */}
                {venuePhotos.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveVenuePhotoIdx(prev => prev <= 0 ? venuePhotos.length - 1 : prev - 1)}
                      style={{
                        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                        width: '40px', height: '40px', fontSize: '1.2rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backdropFilter: 'blur(4px)', transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.7)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
                    >‹</button>
                    <button
                      onClick={() => setActiveVenuePhotoIdx(prev => prev >= venuePhotos.length - 1 ? 0 : prev + 1)}
                      style={{
                        position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                        width: '40px', height: '40px', fontSize: '1.2rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backdropFilter: 'blur(4px)', transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.7)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
                    >›</button>
                    {/* Photo counter */}
                    <div style={{
                      position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 12px',
                      borderRadius: '20px', fontSize: '0.75rem', backdropFilter: 'blur(4px)'
                    }}>
                      {activeVenuePhotoIdx + 1} / {venuePhotos.length}
                    </div>
                  </>
                )}
              </Col>
              <Col lg={6}>
                <div style={{ padding: '2rem' }}>
                  <Badge bg="info" style={{ marginBottom: '0.75rem', fontSize: '0.65rem' }}>VENUE</Badge>
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', marginBottom: '0.5rem' }}>
                    {planData.vendors?.venue?.name}
                  </h2>
                  <div style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                    Capacity: {planData.vendors?.venue?.capacity} guests • {planData.vendors?.venue?.vibe} setting
                  </div>
                  {planData.vendorDatabase?.venue && (
                    <div style={{ marginBottom: '1rem', maxWidth: '360px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                        <span style={{ fontSize: '0.75rem', color: '#d4af37', fontWeight: '600' }}>🔄 Switch venue</span>
                        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>— pick a different option</span>
                      </div>
                      <Form.Select
                        size="sm"
                        value={planData.vendors?.venue?.id || ''}
                        onChange={(e) => swapVendor('venue', e.target.value)}
                        style={{ background: 'rgba(212,175,55,0.08)', color: '#fff', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        {planData.vendorDatabase.venue.map(v => (
                          <option key={v.id} value={v.id} style={{ background: '#1a1a2e', color: '#fff' }}>
                            {v.name} — ${v.price?.toLocaleString()} ({v.capacity} guests)
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  )}
                  <div style={{ fontSize: '1.75rem', fontWeight: '700', color: planData.packageColor, marginBottom: '1rem' }}>
                    ${planData.allocations?.venue?.toLocaleString()}
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {planData.vendors?.venue?.features?.map((f, i) => (
                      <li key={i} style={{ padding: '0.35rem 0', color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: '#10b981' }}>✓</span> {f}
                      </li>
                    ))}
                  </ul>
                  {/* Thumbnail Gallery — click to view in main image */}
                  {venuePhotos.length > 1 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '1rem' }}>
                      {venuePhotos.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt={`${planData.vendors?.venue?.name} ${i + 1}`}
                          onClick={() => setActiveVenuePhotoIdx(i)}
                          style={{
                            width: '70px', height: '50px', objectFit: 'cover', borderRadius: '6px',
                            cursor: 'pointer', transition: 'all 0.2s',
                            border: activeVenuePhotoIdx === i ? '2px solid #d4af37' : '2px solid transparent',
                            opacity: activeVenuePhotoIdx === i ? 1 : 0.6
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          {/* Vendor Cards Grid */}
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: '1.5rem', marginTop: '3rem' }}>
            Your Vendor Team
          </h3>
          <Row className="g-4 mb-5">
            {['catering', 'photography', 'decoration', 'entertainment'].map((category) => {
              const vendor = planData.vendors?.[category];
              if (!vendor) return null;
              const categoryLabels = {
                catering: { icon: '🍽️', label: 'Catering' },
                photography: { icon: '📸', label: 'Photography' },
                decoration: { icon: '💐', label: 'Décor & Florals' },
                entertainment: { icon: '🎵', label: 'Entertainment' }
              };
              const photos = getVendorPhotos(vendor, categoryImages[category]);
              const photoIdx = activeVendorPhotoIdx[category] || 0;
              const currentPhoto = photos[photoIdx] || photos[0];
              return (
                <Col key={category} md={6} lg={3}>
                  <div className="vendor-card">
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={currentPhoto} 
                        alt={vendor.name}
                      />
                      {photos.length > 1 && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveVendorPhotoIdx(prev => ({ ...prev, [category]: photoIdx <= 0 ? photos.length - 1 : photoIdx - 1 })); }}
                            style={{
                              position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)',
                              background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                              width: '28px', height: '28px', fontSize: '0.9rem', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                          >‹</button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveVendorPhotoIdx(prev => ({ ...prev, [category]: photoIdx >= photos.length - 1 ? 0 : photoIdx + 1 })); }}
                            style={{
                              position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                              background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%',
                              width: '28px', height: '28px', fontSize: '0.9rem', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                          >›</button>
                          <div style={{
                            position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 8px',
                            borderRadius: '10px', fontSize: '0.65rem'
                          }}>{photoIdx + 1} / {photos.length}</div>
                        </>
                      )}
                    </div>
                    <div style={{ padding: '1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <Badge bg="secondary" style={{ fontSize: '0.6rem', textTransform: 'uppercase' }}>
                          {categoryLabels[category]?.icon} {categoryLabels[category]?.label}
                        </Badge>
                        <Badge bg={vendor.tier === 'prestige' ? 'warning' : vendor.tier === 'signature' ? 'info' : 'secondary'} style={{ fontSize: '0.6rem' }}>
                          {vendor.tier}
                        </Badge>
                      </div>
                      <h5 style={{ color: '#fff', marginBottom: '0.5rem', fontSize: '1rem' }}>{vendor.name}</h5>
                      {vendor.id === 'included' ? (
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#10b981', marginBottom: '0.75rem' }}>
                          ✓ Included with venue
                        </div>
                      ) : (
                        <>
                        {planData.vendorDatabase?.[category] && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.3rem' }}>
                              <span style={{ fontSize: '0.65rem', color: '#d4af37', fontWeight: '600' }}>🔄 Switch</span>
                            </div>
                            <Form.Select
                              size="sm"
                              value={vendor.id || ''}
                              onChange={(e) => swapVendor(category, e.target.value)}
                              style={{ background: 'rgba(212,175,55,0.08)', color: '#fff', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
                            >
                              {planData.vendorDatabase[category].map(v => (
                                <option key={v.id} value={v.id} style={{ background: '#1a1a2e', color: '#fff' }}>
                                  {v.name} — ${v.pricePerHead ? `${v.pricePerHead}/guest` : v.price?.toLocaleString()}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        )}
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: planData.packageColor, marginBottom: '0.75rem' }}>
                          ${planData.allocations?.[category]?.toLocaleString()}
                          {category === 'catering' && vendor.pricePerHead > 0 && <span style={{ fontSize: '0.75rem', fontWeight: '400', color: 'rgba(255,255,255,0.5)' }}> (${vendor.pricePerHead}/guest)</span>}
                        </div>
                        </>
                      )}
                      <ul style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', paddingLeft: '1rem', margin: 0 }}>
                        {vendor.features?.slice(0, 3).map((f, i) => (
                          <li key={i} style={{ marginBottom: '0.25rem' }}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>

          {/* Budget Breakdown */}
          <div className="plan-card print-break">
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span>📊</span> Budget Breakdown
            </h3>
            
            {/* Visual Bar */}
            <div className="budget-bar">
              {Object.entries(planData.allocations || {}).map(([cat, amount], i) => {
                const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
                const pct = (amount / planData.total) * 100;
                return (
                  <div 
                    key={cat}
                    className="budget-bar-segment"
                    style={{ 
                      width: `${pct}%`, 
                      background: colors[i % colors.length]
                    }}
                  >
                    {pct > 10 ? `${pct.toFixed(0)}%` : ''}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              {Object.entries(planData.allocations || {}).map(([cat], i) => {
                const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
                return (
                  <span key={cat} style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: colors[i % colors.length] }}></span>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </span>
                );
              })}
            </div>

            {/* Line Items */}
            <Row>
              <Col md={8}>
                {Object.entries(planData.allocations || {}).map(([cat, amount], i) => {
                  const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
                  const pct = (amount / planData.total) * 100;
                  return (
                    <div key={cat} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '0.75rem 0',
                      borderBottom: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: colors[i % colors.length] }}></span>
                        <span style={{ color: 'rgba(255,255,255,0.8)', textTransform: 'capitalize' }}>{cat}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>{pct.toFixed(1)}%</span>
                        <span style={{ color: '#fff', fontWeight: '600', minWidth: '80px', textAlign: 'right' }}>${amount.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '1rem 0',
                  borderTop: '2px solid rgba(255,255,255,0.1)',
                  marginTop: '0.5rem'
                }}>
                  <span style={{ fontWeight: '600', color: '#fff' }}>Total</span>
                  <span style={{ fontWeight: '700', fontSize: '1.25rem', color: planData.packageColor }}>${planData.total.toLocaleString()}</span>
                </div>
              </Col>
              <Col md={4}>
                <div style={{ 
                  background: isUnderBudget ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  textAlign: 'center',
                  border: `1px solid ${isUnderBudget ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                    Your Budget
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                    ${planData.budget.toLocaleString()}
                  </div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: isUnderBudget ? '#10b981' : '#ef4444'
                  }}>
                    {isUnderBudget ? `$${savings.toLocaleString()} remaining` : `$${Math.abs(savings).toLocaleString()} over`}
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Day-of Timeline */}
          <div className="plan-card print-break">
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span>📅</span> Example Wedding Day Timeline
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>
              A schedule example for your perfect day. Our planners will customize this to your preferences.
            </p>
            
            <div style={{ maxWidth: '700px' }}>
              {timeline.map((item, i) => (
                <div key={i} className="timeline-item">
                  <div style={{ minWidth: '60px' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#fff' }}>{item.time}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
                      <span style={{ fontWeight: '600', color: '#fff' }}>{item.event}</span>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.9rem' }}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Insights */}
          {planData.aiInsights && planData.aiInsights.length > 0 && (
            <div className="plan-card" style={{ 
              background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(139,92,246,0.1))', 
              border: '1px solid rgba(59,130,246,0.2)'
            }}>
              <h4 style={{ color: '#fff', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span>🤖</span> AI Planning Insights
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {planData.aiInsights.map((insight, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1rem' }}>
                      {insight.type === 'tip' ? '💡' : insight.type === 'upgrade' ? '⬆️' : insight.type === 'vip' ? '👑' : insight.type === 'savings' ? '💰' : 'ℹ️'}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{insight.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="plan-card" style={{ textAlign: 'center', marginTop: '2rem' }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: '1rem' }}>
              Ready to Make It Real?
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', maxWidth: '500px', margin: '0 auto 1.5rem' }}>
              Our expert planners are ready to bring your Bali wedding vision to life. Let's start the conversation.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                onClick={() => setShowContactModal(true)}
                size="lg"
                style={{
                  background: `linear-gradient(135deg, ${planData.packageColor} 0%, ${planData.packageColor}cc 100%)`,
                  border: 'none',
                  borderRadius: '30px',
                  padding: '0.875rem 2rem',
                  fontWeight: '600'
                }}
              >
                💬 Contact a Planner
              </Button>
              <Button
                variant="outline-light"
                size="lg"
                onClick={handleShare}
                style={{ borderRadius: '30px', padding: '0.875rem 2rem' }}
              >
                📤 Share This Plan
              </Button>
            </div>
          </div>
        </Container>

        {/* Sticky CTA Bar */}
        <div className="cta-sticky no-print">
          <Container>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Your {planData.packageName}: </span>
                <span style={{ color: planData.packageColor, fontWeight: '700', fontSize: '1.1rem' }}>${planData.total.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button variant="outline-light" size="sm" onClick={() => navigate('/interactive-planner?edit=true')} style={{ borderRadius: '20px' }}>
                  ← Edit Plan
                </Button>
                <Button variant="outline-light" size="sm" onClick={handlePrint} style={{ borderRadius: '20px' }}>
                  🖨️ Print
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setShowContactModal(true)}
                  style={{ 
                    background: planData.packageColor, 
                    border: 'none', 
                    borderRadius: '20px',
                    fontWeight: '600'
                  }}
                >
                  Book Now →
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
        <Modal.Header closeButton style={{ background: '#1a1a2e', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Modal.Title style={{ color: '#fff' }}>Share Your Wedding Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a2e' }}>
          <p style={{ color: 'rgba(255,255,255,0.7)' }}>Share this link with your partner or family:</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Form.Control 
              readOnly 
              value={typeof window !== 'undefined' ? window.location.href : ''} 
              style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: '#fff' }}
            />
            <Button variant={copied ? 'success' : 'primary'} onClick={copyLink}>
              {copied ? '✓ Copied' : 'Copy'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Contact Modal */}
      <Modal show={showContactModal} onHide={() => { setShowContactModal(false); setContactSent(false); }} centered size="lg">
        <Modal.Header closeButton style={{ background: '#1a1a2e', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Modal.Title style={{ color: '#fff' }}>{contactSent ? '✅ Plan Sent!' : '💬 Let\'s Plan Your Wedding'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a2e' }}>
          {contactSent ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
              <h4 style={{ color: '#fff', marginBottom: '0.5rem' }}>Your wedding plan has been sent!</h4>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>Check your email at <strong style={{ color: '#d4af37' }}>{contactForm.email}</strong> for a copy.</p>
              
              <div style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '16px', padding: '1.5rem', maxWidth: '420px', margin: '0 auto 1.5rem' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>📅</div>
                <h5 style={{ color: '#fff', marginBottom: '0.5rem' }}>Book a Free Consultation</h5>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '1rem' }}>Schedule a 30-min video call with our planning team to discuss your dream wedding</p>
                <Button
                  onClick={() => window.open('https://calendly.com/asrikun/30min', '_blank')}
                  style={{ background: 'linear-gradient(135deg, #d4af37 0%, #b8963e 100%)', border: 'none', borderRadius: '25px', padding: '0.6rem 2rem', fontWeight: '600', color: '#000' }}
                >
                  📅 Schedule on Calendly
                </Button>
              </div>

              <Button
                variant="outline-light"
                onClick={() => { setShowContactModal(false); setContactSent(false); }}
                style={{ borderRadius: '25px', padding: '0.5rem 2rem' }}
              >
                Done
              </Button>
            </div>
          ) : (
          <>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '1.5rem' }}>
            Our team will review your {planData.packageName} plan and reach out within 24 hours.
          </p>
          <Form onSubmit={handleContactSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ color: 'rgba(255,255,255,0.7)' }}>Your Name *</Form.Label>
                  <Form.Control 
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ color: 'rgba(255,255,255,0.7)' }}>Email *</Form.Label>
                  <Form.Control 
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ color: 'rgba(255,255,255,0.7)' }}>Phone / WhatsApp</Form.Label>
                  <Form.Control 
                    value={contactForm.phone}
                    onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ color: 'rgba(255,255,255,0.7)' }}>Preferred Wedding Date</Form.Label>
                  <Form.Control 
                    type="date"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label style={{ color: 'rgba(255,255,255,0.7)' }}>Any questions or special requests?</Form.Label>
                  <Form.Control 
                    as="textarea"
                    rows={3}
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="text-center mt-4">
              <Button 
                type="submit" 
                size="lg"
                style={{
                  background: `linear-gradient(135deg, ${planData.packageColor} 0%, ${planData.packageColor}cc 100%)`,
                  border: 'none',
                  borderRadius: '30px',
                  padding: '0.875rem 3rem',
                  fontWeight: '600'
                }}
              >
                {contactSending ? '📧 Sending...' : 'Send My Plan to a Planner'}
              </Button>
            </div>
          </Form>
          </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WeddingPlanScreen;
